import { DateInput, DateTimeInput, DATE_CHANGE_ERROR_INVALID, DATE_CHANGE_ERROR_OUT_OF_RANGE, getLocaleDateString } from '@cvent/nucleus-core-datetime-utils';
/**

 Copyright (c) 2014 Stripe

 Permission is hereby granted, free of charge, to any person obtaining
 a copy of this software and associated documentation files (the
 "Software"), to deal in the Software without restriction, including
 without limitation the rights to use, copy, modify, merge, publish,
 distribute, sublicense, and/or sell copies of the Software, and to
 permit persons to whom the Software is furnished to do so, subject to
 the following conditions:

 The above copyright notice and this permission notice shall be
 included in all copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
 LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
 WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 **/
var defaultFormat = /(\d{1,4})/g;
var cards = [{
        type: 'Maestro',
        pattern: /^(5(018|0[23]|[68])|6(39|7))/,
        format: defaultFormat,
        length: [12, 13, 14, 15, 16, 17, 18, 19],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'Dankort',
        pattern: /^5019/,
        format: defaultFormat,
        length: [16],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'Visa',
        pattern: /^4/,
        format: defaultFormat,
        length: [13, 16],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'MasterCard',
        pattern: /^(5[1-5]|2[2-7])/,
        format: defaultFormat,
        length: [16],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'AmericanExpress',
        pattern: /^3[47]/,
        format: /(\d{1,4})(\d{1,6})?(\d{1,5})?/,
        length: [15],
        cvcLength: [3, 4],
        luhn: true
    }, {
        type: 'DinersClub',
        pattern: /^3[0689]/,
        format: /(\d{1,4})(\d{1,6})?(\d{1,4})?/,
        length: [14],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'Discover',
        pattern: /^6([045]|22)/,
        format: defaultFormat,
        length: [16],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'UnionPay',
        pattern: /^(62|88)/,
        format: defaultFormat,
        length: [16, 17, 18, 19],
        cvcLength: [3],
        luhn: false
    }, {
        type: 'JCB',
        pattern: /^35/,
        format: defaultFormat,
        length: [16],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'UATP',
        pattern: /^1/,
        format: defaultFormat,
        length: [15],
        cvcLength: [3],
        luhn: true
    }, {
        type: 'Laser',
        pattern: /^(6304|6706|6771|6709)/,
        format: defaultFormat,
        length: [16],
        cvcLength: 3,
        luhn: true
    }, {
        type: 'Solo',
        pattern: /^(6334|6767)/,
        format: defaultFormat,
        length: [16, 18, 19],
        cvcLength: 3,
        luhn: true
    }];
function lunhCheck(num) {
    var digit;
    var digits = (num + '').split('').reverse();
    var odd = true;
    var sum = 0;
    for (var i = 0; i < digits.length; i++) {
        digit = digits[i];
        digit = parseInt(digit, 10);
        odd = !odd;
        if (odd) {
            digit *= 2;
        }
        if (digit > 9) {
            digit -= 9;
        }
        sum += digit;
    }
    return sum % 10 === 0;
}
function cardFromType(type) {
    var card;
    for (var i = 0; i < cards.length; i++) {
        card = cards[i];
        if (card.type === type) {
            return card;
        }
    }
}
function cardFromNumber(number) {
    var num = (number + '').replace(/\D/g, '');
    var card;
    for (var i = 0; i < cards.length; i++) {
        card = cards[i];
        if (card.pattern.test(num)) {
            return card;
        }
    }
}
/**
 * Validate credit card verification value based on card type
 */
function validateCardCvc(cvcVal, type) {
    var cvc = cvcVal.trim();
    if (!/^\d+$/.test(cvc)) {
        return false;
    }
    var card = cardFromType(type);
    return card !== undefined ? card.cvcLength.indexOf(cvc.length) >= 0 : cvc.length >= 3 && cvc.length <= 4;
}
/**
 * Validate credit card number based on card type
 */
function validateCardNumber(number, selectedType) {
    var num = (number + '').replace(/\s+|-/g, '');
    if (!/^\d+$/.test(num)) {
        return false;
    }
    var card = cardFromNumber(num);
    if (!card || selectedType && selectedType !== card.type) {
        return false;
    }
    return card.length.indexOf(num.length) >= 0 && (card.luhn === false || lunhCheck(num));
}
/**
 * Validates whether the value exists or not. The value must not be empty or completely white-space.
 */
var requiredValidator = function requiredValidator(value) {
    return !!value && (typeof value !== 'string' || !!value.trim());
};
/**
 * Validates if a string is at least as long as the the specified length.
 * @param length The minimum length the string can be.
 */
var minLengthValidator = function minLengthValidator(length) {
    return function (value) {
        return requiredValidator(value) && value.trim().length >= length;
    };
};
/**
 * Validates if a string is longer than the the specified length.
 * @param length The maximum length the string can be.
 */
var maxLengthValidator = function maxLengthValidator(length) {
    return function (value) {
        return requiredValidator(value) && value.trim().length <= length;
    };
};
/**
 * Validates if the text only contains digits
 * Accepts -3 -.3 -3. -3.0 and all the positive variants
 * @param text The string to validate
 */
var isSupportedNumber = function isSupportedNumber(text) {
    var pattern = /(^[-]?([0-9.]+)([0-9.]+)?)$/;
    return pattern.test(text);
};
/**
 * Validates if a string is a number within a certain range
 * @param min The minimum numeric value
 * @param max The maximum numeric value
 */
var numberValidator = function numberValidator(min, max) {
    return function (value) {
        if (!requiredValidator(value)) {
            return false;
        }
        var numValue = Number(value.trim());
        return isSupportedNumber(value) && !value.includes('.') && (typeof min !== 'number' || numValue >= min) && (typeof max !== 'number' || numValue <= max);
    };
};
/**
 * Validates if a string is a decimal value
 */
var decimalValidator = function decimalValidator(value) {
    // Check for 0 or 1 decimal points
    return requiredValidator(value) && isSupportedNumber(value) && isFinite(value);
};
/**
 * Validates if a string is a currency value i.e. if it is a non-negative decimal
 */
var currencyValidator = function currencyValidator(value) {
    // Check for optional groups of three separated by a comma (or decimal for Euro),
    // followed by an optional group of a decimal (or comma for Euro) followed by  0 - 2 numbers
    var currencyFormat = /^[1-9]\d*(((,\d{3})*)?(\.\d{0,2})?)$/;
    var euroFormat = /^[1-9]\d*(((\.\d{3})*)?(,\d{0,2})?)$/;
    return requiredValidator(value) && (currencyFormat.test(value) || euroFormat.test(value));
};
/**
 * Validates if a value is a US Phone Number (999-999-9999)
 */
var usPhoneNumberValidator = function usPhoneNumberValidator(value) {
    return requiredValidator(value) && /^\s*\d{3}-\d{3}-\d{4}\s*$/.test(value);
};
/**
 * Validates if a value is a Social Security Number (123-45-6789)
 */
var socialSecurityNumberValidator = function socialSecurityNumberValidator(value) {
    return requiredValidator(value) && /^\s*\d{3}-\d{2}-\d{4}\s*$/.test(value);
};
/**
 * Verfies the email address matches a basic format. It is understood that this
 * validation does not cover every aspect of the email format which should
 * be handled server side.
 */
var validEmailRegex = /^(?:[\0-\t\x0B\f\x0E-\u2027\u202A-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])+@(?:[\0-\t\x0B\f\x0E-\u2027\u202A-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])+\.(?:[\0-\t\x0B\f\x0E-\u2027\u202A-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])+$/;
var emailFormatValidator = function emailFormatValidator(value) {
    return requiredValidator(value) && validEmailRegex.test(value.trim());
};
function optionalValidator(validator) {
    return function (value, additionalInformation) {
        return requiredValidator(value) ? validator(value, additionalInformation) : true;
    };
}
/**
 * Verifies that a given value is a valid credit format based on the credit card type
 */
var creditCardValidator = function creditCardValidator(cardType) {
    return function (value) {
        return requiredValidator(value) && validateCardNumber(value, cardType);
    };
};
function dateRequiredValidator(dateInput) {
    if (typeof dateInput === 'string' || dateInput instanceof Date) {
        return true;
    }
    else if (dateInput instanceof DateInput || dateInput instanceof DateTimeInput) {
        return dateInput.dateText && dateInput.dateText.trim();
    }
    return false;
}
function dateFormatValidator(dateInput) {
    return !(dateInput instanceof DateInput && dateInput.statusCode === DATE_CHANGE_ERROR_INVALID);
}
function dateRangeValidator(dateInput) {
    return !(dateInput instanceof DateInput && dateInput.statusCode === DATE_CHANGE_ERROR_OUT_OF_RANGE);
}
function timeFormatValidator(dateInput, useSeconds) {
    if (!(dateInput instanceof DateInput && dateInput.date)) {
        return true;
    }
    if (!dateInput.timeText) {
        return false;
    }
    var _dateInput$timeText = dateInput.timeText, hour = _dateInput$timeText.hour, minute = _dateInput$timeText.minute, second = _dateInput$timeText.second;
    return typeof hour === 'number' && typeof minute === 'number' && (!useSeconds || typeof second === 'number');
}
/**
 * Get plain text from a React element that dangerously sets inner html. E.g. use this to convert a user's rich text
 * label for a field into text to use in a validation message
 */
function plainTextFromDangerouslySetInnerHtml(element) {
    if (typeof element === 'string') {
        return element;
    }
    var html = element.props.dangerouslySetInnerHTML.__html;
    var domNode = global.document.createElement('div');
    domNode.innerHTML = html;
    return domNode.innerText;
}
var BlankChoice = Symbol('BlankChoice');
var NAChoice = Symbol('NAChoice');
var OtherChoice = Symbol('OtherChoice');
var choiceRequired = function choiceRequired(translate) {
    var _errorMessage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'NucleusWidgets_Validations_RequiredField__resx';
    return {
        validator: function validator(options) {
            if (options.hasOwnProperty('selectedValue')) {
                return options.selectedValue && options.selectedValue !== BlankChoice;
            }
            return options.selectedValues && options.selectedValues.length > 0;
        },
        errorMessage: function errorMessage(value, field) {
            return translate(_errorMessage, {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var choicesNumberSelected = function choicesNumberSelected(translate, min, max) {
    return {
        validator: function validator(options) {
            if (options.selectedValues.includes(NAChoice)) {
                return true;
            }
            return options.selectedValues && (options.selectedValues.length === 0 || (typeof min !== 'number' || options.selectedValues.length >= min) && (typeof max !== 'number' || options.selectedValues.length <= max));
        },
        errorMessage: function errorMessage() {
            var message;
            if (typeof min === 'number' && typeof max === 'number') {
                message = min === max ? 'NucleusWidgets_Validations_NumberOfChoicesSelectedExactly__resx' : 'NucleusWidgets_Validations_NumberOfChoicesSelectedBetween__resx';
            }
            else if (typeof min === 'number') {
                message = 'NucleusWidgets_Validations_NumberOfChoicesAtLeast__resx';
            }
            else if (typeof max === 'number') {
                message = 'NucleusWidgets_Validations_NumberOfChoicesAtMost__resx';
            }
            return translate(message, {
                min: typeof min === 'number' ? min.toString() : min,
                max: typeof max === 'number' ? max.toString() : max
            });
        }
    };
};
var textRequired = function textRequired(translate) {
    var _errorMessage2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'NucleusWidgets_Validations_RequiredField__resx';
    return {
        validator: requiredValidator,
        errorMessage: function errorMessage(value, field) {
            return translate(_errorMessage2, {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var textMinLength = function textMinLength(translate, minLength) {
    return {
        validator: optionalValidator(minLengthValidator(minLength)),
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_MinLengthField__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field),
                length: minLength
            });
        }
    };
};
var textMaxLength = function textMaxLength(translate, maxLength) {
    return {
        validator: optionalValidator(maxLengthValidator(maxLength)),
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_MaxLengthField__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field),
                length: maxLength
            });
        }
    };
};
var textNumberInRange = function textNumberInRange(translate, min, max) {
    return {
        validator: optionalValidator(numberValidator(min, max)),
        errorMessage: function errorMessage(value, field) {
            var message;
            if (typeof min === 'number' && typeof max === 'number') {
                message = 'NucleusWidgets_Validations_NumberFormatFieldBetween__resx';
            }
            else if (typeof min === 'number') {
                message = 'NucleusWidgets_Validations_NumberFormatFieldGreaterThan__resx';
            }
            else if (typeof max === 'number') {
                message = 'NucleusWidgets_Validations_NumberFormatFieldLessThan__resx';
            }
            else {
                message = 'NucleusWidgets_Validations_NumberFormatFieldAny__resx';
            }
            return translate(message, {
                field: plainTextFromDangerouslySetInnerHtml(field),
                min: typeof min === 'number' ? min.toString() : min,
                max: typeof max === 'number' ? max.toString() : max
            });
        }
    };
};
var textCurrency = function textCurrency(translate) {
    return {
        validator: optionalValidator(currencyValidator),
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_CurrencyFormat__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var textDecimal = function textDecimal(translate) {
    return {
        validator: optionalValidator(decimalValidator),
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_DecimalFormat__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var textUSPhoneNumber = function textUSPhoneNumber(translate) {
    return {
        validator: optionalValidator(usPhoneNumberValidator),
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_USPhoneNumberFormat__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var textEmailAddress = function textEmailAddress(translate) {
    return {
        validator: optionalValidator(emailFormatValidator),
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_EmailFormatField__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var textSocialSecurityNumber = function textSocialSecurityNumber(translate) {
    return {
        validator: optionalValidator(socialSecurityNumberValidator),
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_SocialSecurityNumberFormat__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var dateRequired = function dateRequired(translate) {
    var _errorMessage3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'NucleusWidgets_Validations_RequiredField__resx';
    return {
        validator: dateRequiredValidator,
        errorMessage: function errorMessage(value, field) {
            return translate(_errorMessage3, {
                field: plainTextFromDangerouslySetInnerHtml(field)
            });
        }
    };
};
var dateFormat = function dateFormat(translate, locale, formatOptions) {
    return {
        validator: dateFormatValidator,
        errorMessage: function errorMessage() {
            return translate('NucleusWidgets_Validations_DateFormat__resx', {
                format: getLocaleDateString(locale, formatOptions)
            });
        }
    };
};
var dateRange = function dateRange(translate, minDate, maxDate) {
    return {
        validator: dateRangeValidator,
        errorMessage: function errorMessage() {
            if (minDate && maxDate) {
                return translate('NucleusWidgets_Validations_DateBetween__resx', {
                    startDate: new Date(minDate),
                    endDate: new Date(maxDate)
                });
            }
            else if (maxDate) {
                return translate('NucleusWidgets_Validations_DateBefore__resx', {
                    endDate: new Date(maxDate)
                });
            }
            else if (minDate) {
                return translate('NucleusWidgets_Validations_DateAfter__resx', {
                    startDate: new Date(minDate)
                });
            }
        }
    };
};
var dateTimeGroupValidations = function dateTimeGroupValidations(translate, required, useSeconds, locale, formatOptions, minDate, maxDate) {
    var requiredErrorMessage = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : 'NucleusWidgets_Validations_RequiredField__resx';
    return {
        validator: function validator(dateInput) {
            return (!required || dateRequiredValidator(dateInput)) && dateFormatValidator(dateInput) && timeFormatValidator(dateInput, useSeconds) && dateRangeValidator(dateInput);
        },
        errorMessage: function errorMessage(dateInput, field) {
            if (required && !dateRequiredValidator(dateInput)) {
                return {
                    required: translate(requiredErrorMessage, {
                        field: plainTextFromDangerouslySetInnerHtml(field)
                    })
                };
            }
            if (!dateFormatValidator(dateInput)) {
                return {
                    dateFormatInvalid: translate('NucleusWidgets_Validations_DateFormat__resx', {
                        format: getLocaleDateString(locale, formatOptions)
                    })
                };
            }
            if (!timeFormatValidator(dateInput, useSeconds)) {
                return {
                    timeFormatInvalid: translate('NucleusWidgets_Validations_TimeRequired__resx', {
                        field: plainTextFromDangerouslySetInnerHtml(field)
                    })
                };
            }
            if (!dateRangeValidator(dateInput)) {
                if (minDate && maxDate) {
                    return {
                        dateRange: translate('NucleusWidgets_Validations_DateBetween__resx', {
                            startDate: new Date(minDate),
                            endDate: new Date(maxDate)
                        })
                    };
                }
                else if (maxDate) {
                    return {
                        dateRange: translate('NucleusWidgets_Validations_DateBefore__resx', {
                            endDate: new Date(maxDate)
                        })
                    };
                }
                else if (minDate) {
                    return {
                        dateRange: translate('NucleusWidgets_Validations_DateAfter__resx', {
                            startDate: new Date(minDate)
                        })
                    };
                }
            }
        }
    };
};
var creditCardNumber = function creditCardNumber(translate, cardName) {
    return {
        validator: function validator(value, cardType) {
            return creditCardValidator(cardType)(value);
        },
        errorMessage: function errorMessage(value, field) {
            return translate('NucleusWidgets_Validations_CreditCardFormatFormat__resx', {
                field: plainTextFromDangerouslySetInnerHtml(field),
                cardName: cardName
            });
        }
    };
};
export { BlankChoice, NAChoice, OtherChoice, choiceRequired, choicesNumberSelected, creditCardNumber, creditCardValidator, currencyValidator, dateFormat, dateFormatValidator, dateRange, dateRangeValidator, dateRequired, dateRequiredValidator, dateTimeGroupValidations, decimalValidator, emailFormatValidator, maxLengthValidator, minLengthValidator, numberValidator, optionalValidator, plainTextFromDangerouslySetInnerHtml, requiredValidator, socialSecurityNumberValidator, textCurrency, textDecimal, textEmailAddress, textMaxLength, textMinLength, textNumberInRange, textRequired, textSocialSecurityNumber, textUSPhoneNumber, timeFormatValidator, usPhoneNumberValidator, validateCardCvc, validateCardNumber };
